import { useState, useRef, useEffect } from 'react';
import { AutomatedBreakType } from '@sdk/settings/types';
import useModalState from '@sdk/components/ui/modal/useModalState';
import type { Shift } from '@sdk/automatedBreaks/types/Shift';

import useIsAutomatedBreaksFeatureFlagEnabled from '@web/automatedBreaks/hooks/useIsAutomatedBreaksFeatureFlagEnabled';
import canTakeBreak from '@web/timeClock/utils/canTakeBreak';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import { OnStartBreakProps } from '@web/timeClock/ChooseBreakTypeModal/chooseBreakTypeModal.types';

const getDefaultSelection = ({ meal, rest }) => {
  // When two options are available we don't do preselection
  if (meal.isEnabled && rest.isEnabled) {
    return null;
  }

  if (meal.isEnabled) {
    return AutomatedBreakType.meal;
  }

  if (rest.isEnabled) {
    return AutomatedBreakType.rest;
  }

  return null;
};

export const useAutomatedBreakType = () => {
  const { automatedBreaks } = useAutomatedBreaksSettings();
  const defaultSelection = getDefaultSelection(automatedBreaks);
  const [automatedType, setAutomatedType] = useState(defaultSelection);

  return {
    automatedType,
    setAutomatedType,
    isMealBreakEnabled: automatedBreaks.meal.isEnabled,
    isRestBreakEnabled: automatedBreaks.rest.isEnabled,
    automatedBreaksSettings: automatedBreaks,
  };
};

type Props = {
  onStartBreak: (props: OnStartBreakProps) => Promise<void>;
  shift: Shift;
};
export const useChooseBreakTypeModal = ({ shift, onStartBreak }: Props) => {
  const { isVisible, open, close } = useModalState();
  const {
    automatedType,
    setAutomatedType,
    isMealBreakEnabled,
    isRestBreakEnabled,
    automatedBreaksSettings,
  } = useAutomatedBreakType();
  const resolveRef = useRef<VoidFunction>();
  const rejectRef = useRef<VoidFunction>();
  const automatedBreaksFeatureEnabled = useIsAutomatedBreaksFeatureFlagEnabled();
  const [breakAllowance, setBreakAllowance] = useState(
    canTakeBreak({
      automatedBreaksFeatureFlag: automatedBreaksFeatureEnabled,
      automatedBreaksSettings,
      shift,
    })
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setBreakAllowance(
        /**
         * We need to force check this periodically because the result of the following
         * function depends on current local time
         */
        canTakeBreak({
          automatedBreaksFeatureFlag: automatedBreaksFeatureEnabled,
          automatedBreaksSettings,
          shift,
        })
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [automatedBreaksFeatureEnabled, automatedBreaksSettings, shift]);

  const canShowStartBreakButton = breakAllowance.isAllowed;
  const canTakeMealBreak = isMealBreakEnabled && breakAllowance.meal;
  const canTakeRestBreak = isRestBreakEnabled && breakAllowance.rest;

  useEffect(() => {
    if (canTakeMealBreak && !canTakeRestBreak) {
      setAutomatedType(AutomatedBreakType.meal);
    }

    if (!canTakeMealBreak && canTakeRestBreak) {
      setAutomatedType(AutomatedBreakType.rest);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = () => {
    open();
    return new Promise<void>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });
  };

  const startBreak = async () => {
    await onStartBreak({ automatedType });
    resolveRef.current();
  };

  const cancel = () => {
    rejectRef.current();
    close();
  };

  return {
    isVisible,
    automatedType,
    setAutomatedType,
    canTakeMealBreak,
    canTakeRestBreak,
    canShowStartBreakButton,
    onClick,
    startBreak,
    cancel,
  };
};
